import { useEffect, useMemo, useState } from "react";
import { Calendar, CreditCard, Check } from "lucide-react";
import { useLocation } from "wouter";
import { PageNavigation } from "../components/ui/page-navigation";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import SeatManagementUI from "../components/billing/SeatManagementUI";
import {
  PLAN_OPTIONS,
  PLAN_TYPES,
  PRICING,
} from "../constants/subscriptionPricing";
import {
  calculateBillingSummary,
  createInitialSeats,
  createInitialSubscription,
  formatCurrency,
  formatDisplayDate,
  formatPlanLabel,
} from "../lib/subscriptionBilling";
import { saveCheckoutSession } from "../lib/checkoutSession";

const billingCardClass =
  "overflow-hidden rounded-xl border-0 bg-white p-0 shadow-lg transition-shadow";
const billingHeaderClass =
  "w-full shrink-0 bg-blue-600 px-6 py-6 text-white rounded-t-xl";

export default function SubscriptionPlans() {
  const [, setLocation] = useLocation();
  const [activeSubscription] = useState(
    createInitialSubscription
  );
  const [selectedPlanType, setSelectedPlanType] = useState(
    PLAN_TYPES.WITHOUT_EHR
  );
  const [seats, setSeats] = useState(createInitialSeats);

  useEffect(() => {
    document.title = "Subscription - Seismic Connect";
  }, []);

  const billingSummary = useMemo(
    () => calculateBillingSummary({ planType: selectedPlanType, seats }),
    [selectedPlanType, seats]
  );

  const activeSummary = useMemo(
    () =>
      calculateBillingSummary({
        planType: activeSubscription.planType,
        seats,
      }),
    [activeSubscription.planType, seats]
  );

  const renewalDisplay = formatDisplayDate(activeSubscription.renewalDate);

  const handleInviteSeat = (email, role) => {
    if (seats.length >= PRICING.maxSupportingSeats) return;
    setSeats((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        email,
        role,
        invitedAt: new Date().toISOString(),
      },
    ]);
  };

  const handleRemoveSeat = (seatId) => {
    setSeats((prev) => prev.filter((s) => s.id !== seatId));
  };

  const handleProceedToCheckout = () => {
    saveCheckoutSession({ planType: selectedPlanType, seats });
    setLocation("/settings/payment-billing");
  };

  return (
    <div className="px-4 pb-6">
      <div className="max-w-6xl mx-auto space-y-6">
        <PageNavigation
          title="Subscription Overview"
          subtitle="Choose your monthly plan and manage supporting user seats"
        />

        <section className="space-y-3" data-tour="subscription-plans">
          <h2 className="text-lg font-semibold text-gray-900">Choose your plan</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {PLAN_OPTIONS.map((plan) => {
              const isSelected = selectedPlanType === plan.id;
              return (
                <button
                  key={plan.id}
                  type="button"
                  onClick={() => setSelectedPlanType(plan.id)}
                  className={`text-left p-5 rounded-xl border-2 transition-all duration-200 ${
                    isSelected
                      ? "border-blue-500 bg-blue-50 shadow-md"
                      : "border-gray-200 hover:border-gray-300 bg-white"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold text-gray-900">{plan.title}</p>
                      <p className="text-sm text-gray-600 mt-1">{plan.description}</p>
                    </div>
                    {isSelected && (
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 text-white shrink-0">
                        <Check className="w-4 h-4" />
                      </span>
                    )}
                  </div>
                  <p className="mt-4 text-2xl font-bold text-gray-900">
                    {formatCurrency(plan.price)}
                    <span className="text-sm font-normal text-gray-600">/month</span>
                  </p>
                </button>
              );
            })}
          </div>
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-tour="subscription-usage">
          <Card className={billingCardClass}>
            <div className={billingHeaderClass}>
              <h3 className="text-xl font-bold text-white">Current Plan</h3>
              <p className="text-sm mt-1 text-blue-100">Your active subscription</p>
            </div>
            <CardContent className="p-6 pt-6 space-y-4">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                {formatPlanLabel(activeSubscription.planType)}
              </span>

              <div className="flex items-start gap-3 text-gray-700">
                <Calendar className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500">Renewal date</p>
                  <p className="font-semibold text-gray-900">{renewalDisplay}</p>
                </div>
              </div>

              <div className="space-y-2 text-sm border-t border-gray-100 pt-4">
                <div className="flex justify-between">
                  <span className="text-gray-600">Doctor (included)</span>
                  <span className="font-medium text-gray-900">1</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Supporting seats</span>
                  <span className="font-medium text-gray-900">{seats.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Active plan total</span>
                  <span className="font-semibold text-gray-900">
                    {activeSummary.billingLabel}
                  </span>
                </div>
              </div>

              <Button
                type="button"
                onClick={handleProceedToCheckout}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Proceed to checkout
              </Button>
            </CardContent>
          </Card>

          <Card className={billingCardClass}>
            <div className={billingHeaderClass}>
              <h3 className="text-xl font-bold text-white">Order Preview</h3>
              <p className="text-sm mt-1 text-blue-100">
                Updates when you change plan or seats
              </p>
            </div>
            <CardContent className="p-6 pt-6 space-y-4">
              {billingSummary.lineItems.map((item) => (
                <div key={item.id} className="flex justify-between text-sm py-1">
                  <span className="text-gray-600">{item.label}</span>
                  <span className="font-medium text-gray-900">
                    {formatCurrency(item.amount)}
                    <span className="text-gray-500 font-normal">/mo</span>
                  </span>
                </div>
              ))}

              <div className="border-t border-gray-200 pt-4 flex justify-between items-center">
                <span className="font-bold text-gray-900">Total</span>
                <div className="text-right">
                  <span className="font-bold text-xl text-gray-900">
                    {formatCurrency(billingSummary.total)}
                  </span>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {billingSummary.billingLabel}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <SeatManagementUI
          seats={seats}
          maxSeats={PRICING.maxSupportingSeats}
          seatPricePerMonth={PRICING.seatPerMonth}
          onInviteSeat={handleInviteSeat}
          onRemoveSeat={handleRemoveSeat}
        />
      </div>
    </div>
  );
}
